import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import firebaseConfig from '../../firebaseConfig';
import { initializeApp, getApps } from 'firebase/app';
import {child, get, getDatabase, onValue, ref, off} from "firebase/database";
import {getAuth, signInAnonymously} from "firebase/auth";

import {useDispatch} from 'react-redux'
import { setTeamDetails, setTeammateInfo, setCurrentTeamId, resetTeam } from "../../redux/teamSlice";
import { setUidDetails, updateUid } from "../../redux/userSlice";
import CircularProgress from "@mui/material/CircularProgress";
import {Flex} from "reflexbox";
import {appBackgroundColor} from "./ThemeSettings";
import {messageType} from "../common/RequestAlertBar";
import {updateConnection} from "../../redux/platformSlice";


const WithFirebaseAnonUser = props => {
  const dispatch = useDispatch();
  if (getApps().length === 0) {
    initializeApp(firebaseConfig);
  }
  const [loaded, setLoaded] = useState(false);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [uidDetailsLoaded, setUidDetailsLoaded] = useState(false);


  const {currentTeamId, teamDetails, uidInRedux, connection} = props;
  const currentMemberIdsInRedux = teamDetails?.memberIds ? [...teamDetails.memberIds] : []

  const auth = getAuth();

  useEffect(() => {
    signInAnonymously(auth)
      .then((user) => {
        // Signed in..
        const uid = user.user.uid;
        uid && dispatch(updateUid(uid));
        const db = getDatabase();
        const currentUidRef = ref(db, `users/${uid}`);
        const unsubscribe = onValue(currentUidRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            dispatch(setUidDetails(data));
            dispatch(setCurrentTeamId(data.currentTeamId ?? ''));
          }
          setUidDetailsLoaded(true)
        });
        setLoaded(true)
        
        // Return cleanup function
        return () => {
          off(currentUidRef, 'value', unsubscribe);
        };
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        // ...
      });

  }, []);

  //listener to determine online/offline status
  useEffect(()=>{
    const db = getDatabase();
    const connectedRef = ref(db, ".info/connected");
    const unsubscribe = onValue(connectedRef, (snap) => {
      if (snap.val() === true) {
        dispatch(updateConnection({connection: true}));
      } else {
        dispatch(updateConnection({connection: false}));
      }
    });
    
    return () => {
      off(connectedRef, 'value', unsubscribe);
    };
  },[]);

  //setup listeners for everything under teamID
  useEffect(() => {
    if (currentTeamId) {
      const db = getDatabase();
      const currentTeamIdref = ref(db, 'teams/' + currentTeamId);
      let memberIds = [];

      const unsubscribe = onValue(currentTeamIdref, (snapshot) => {
        const data = snapshot.val();
        memberIds = [...data?.memberIds || ''];
        dispatch(setTeamDetails(data));
        setTeamsLoaded(true);
      });
      
      return () => {
        off(currentTeamIdref, 'value', unsubscribe);
      };
    } else {
      dispatch(resetTeam());
      setTeamsLoaded(true);
    }
  }, [currentTeamId, connection]);

  //setup listeners for each team member's names
  //Austin would it be better if I just created an array of names under the teamID? or is setting up multiple listeners better?
  useEffect(() => {
    const db = getDatabase();
    const unsubscribes = [];
    
    if (teamDetails?.memberIds) {
      teamDetails.memberIds.forEach((memberId) => {
        const path = `users/${memberId}`;
        const teammateInfoRef = ref(db, path);

        const unsubscribe = onValue(teammateInfoRef, (snapshot) => {
          const data = snapshot.val();
          dispatch(setTeammateInfo({
            uid: memberId,
            name: data?.name || 'Anonymous',
            color: data?.color || null,
            character: data?.character || 0,
          }));
        });
        
        unsubscribes.push(() => off(teammateInfoRef, 'value', unsubscribe));
      });
    }

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [teamDetails?.memberIds]);


  const loadingSpinner = <Flex style={{
    width: '100%',
    height: '100%',
    backgroundColor: appBackgroundColor,
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <CircularProgress size={70}/>
  </Flex>;

  return (<>
    {(loaded && uidDetailsLoaded && teamsLoaded) ? props.children : loadingSpinner}
  </>)
};

WithFirebaseAnonUser.propTypes = {
  children: PropTypes.node.isRequired
};

export default WithFirebaseAnonUser;
